import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, img } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export const PageHead = ({ title, sub, action }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <h1 className="font-display text-[40px] font-semibold leading-none">{title}</h1>
      {sub && <p className="text-[15px] text-plum/65 mt-2">{sub}</p>}
    </div>
    {action}
  </div>
);

export const Panel = ({ title, children, className = "", sub }) => (
  <section className={`rounded-[22px] bg-white border border-plum/10 p-5 sm:p-7 ${className}`}>
    {title && <h2 className="font-display text-[26px] font-semibold leading-tight">{title}</h2>}
    {sub && <p className="text-[14px] text-plum/60 mt-1">{sub}</p>}
    <div className={title ? "mt-5" : ""}>{children}</div>
  </section>
);

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[14px] font-bold mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-[12px] text-plum/55 mt-1">{hint}</span>}
  </label>
);

export const Btn = ({ children, variant = "dark", className = "", ...p }) => {
  const v = {
    dark: "bg-plum text-pearl hover:bg-berry",
    berry: "bg-berry text-pearl hover:bg-plum",
    light: "bg-white border border-plum/15 hover:border-plum",
    green: "bg-emerald text-white hover:bg-[#1f5242]",
    red: "bg-white border border-ruby/40 text-ruby hover:bg-ruby hover:text-white",
  }[variant];
  return <button type="button" className={`inline-flex items-center justify-center gap-2 h-11 px-4 rounded-[14px] text-[13px] font-extrabold uppercase tracking-[0.1em] transition-colors disabled:opacity-50 ${v} ${className}`} {...p}>{children}</button>;
};

export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd);
  return data.url;
};

export const ImageField = ({ label, value, onChange, testId, hint }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); toast.success("Image uploaded"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div>
      <span className="block text-[14px] font-bold mb-1.5">{label}</span>
      <div className="flex gap-3 items-center">
        <div className="w-24 h-24 rounded-[14px] bg-pearl border border-plum/10 overflow-hidden flex-none grid place-items-center">
          {value ? <img src={img(value)} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-6 h-6 text-plum/30" />}
        </div>
        <div className="space-y-2 flex-1 min-w-0">
          <label className="inline-flex items-center gap-2 h-10 px-4 rounded-[12px] bg-plum text-pearl text-[12px] font-extrabold uppercase tracking-wider cursor-pointer hover:bg-berry" data-testid={testId}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {value ? "Replace" : "Upload"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} />
          </label>
          {value && <button type="button" onClick={() => onChange("")} className="ml-2 text-[13px] font-semibold text-ruby">Remove</button>}
          {hint && <p className="text-[12px] text-plum/55">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

export const ImagesField = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    for (const f of files) {
      try { urls.push(await uploadImage(f)); } catch (err) { toast.error(errMsg(err)); }
    }
    onChange([...value, ...urls]);
    setBusy(false);
    e.target.value = "";
  };
  const move = (i, d) => { const n = [...value]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <div>
      <span className="block text-[14px] font-bold mb-1.5">Photographs <span className="font-medium text-plum/55">(first photo is the main image)</span></span>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {value.map((u, i) => (
          <div key={u + i} className="relative rounded-[14px] overflow-hidden aspect-[4/5] bg-pearl group" data-testid={`product-image-${i}`}>
            <img src={img(u)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute left-1.5 top-1.5 rounded-md bg-plum text-pearl text-[10px] font-bold px-1.5 py-0.5">MAIN</span>}
            <div className="absolute inset-x-1.5 bottom-1.5 flex justify-between">
              <div className="flex gap-1">
                {i > 0 && <button type="button" onClick={() => move(i, -1)} className="w-7 h-7 rounded-md bg-white/95 grid place-items-center" aria-label="Move earlier"><ArrowUp className="w-3.5 h-3.5 -rotate-90" /></button>}
                {i < value.length - 1 && <button type="button" onClick={() => move(i, 1)} className="w-7 h-7 rounded-md bg-white/95 grid place-items-center" aria-label="Move later"><ArrowDown className="w-3.5 h-3.5 -rotate-90" /></button>}
              </div>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="w-7 h-7 rounded-md bg-white/95 text-ruby grid place-items-center" aria-label="Remove image"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        <label className="rounded-[14px] aspect-[4/5] border-2 border-dashed border-plum/20 grid place-items-center cursor-pointer hover:border-plum text-center p-2" data-testid="product-images-upload">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <span className="text-[13px] font-bold"><ImagePlus className="w-6 h-6 mx-auto mb-1" />Add photos</span>}
          <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={add} />
        </label>
      </div>
    </div>
  );
};

export const ActionDialog = ({ open, onClose, title, description, confirmLabel, variant = "dark", withNote, notePlaceholder, onConfirm, testId }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setNote(""); }, [open]);
  const go = async () => { setBusy(true); try { await onConfirm(note); onClose(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[22px] bg-pearl max-w-md" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-display text-[28px]">{title}</DialogTitle>{description && <DialogDescription className="text-[15px] text-plum/75">{description}</DialogDescription>}</DialogHeader>
        {withNote && <textarea rows={3} className="rh-input" placeholder={notePlaceholder} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${testId}-note`} />}
        <DialogFooter className="gap-2"><Btn variant="light" onClick={onClose}>Cancel</Btn><Btn variant={variant} onClick={go} disabled={busy} data-testid={`${testId}-confirm`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}{confirmLabel}</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const useSettings = () => {
  const { refresh } = useStore();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const set = useCallback((section, key) => (v) => setS((p) => ({ ...p, [section]: { ...p[section], [key]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v } })), []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", s); setS(data); await refresh(); toast.success("Saved — your storefront is updated"); } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  return { s, set, save, saving };
};

export const SaveBar = ({ save, saving }) => (
  <div className="sticky bottom-4 z-10 flex justify-end mt-8">
    <Btn variant="berry" onClick={save} disabled={saving} className="h-14 px-8 shadow-[0_16px_40px_-16px_rgba(119,56,78,.8)]" data-testid="admin-save-button">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Save changes</Btn>
  </div>
);
